import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Phone, Shield, Award, Users, CheckCircle, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { TestimonialsCarousel } from "@/components/site/TestimonialsCarousel";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import logoAutosurMarquePreferee from "@/assets/logo-autosur-marque-preferee.webp";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema } from "@/components/SchemaOrg";
import scootersAccueil from "@/assets/centre/scooters-accueil.webp";
import audiRs6Banc from "@/assets/centre/audi-rs6-banc.webp";
import facadeCentreEntree from "@/assets/centre/facade-centre-entree.webp";

// Galerie photos Google My Business
import accueilReception from "@/assets/centre/accueil-reception.webp";
import ambulancesMercedes from "@/assets/centre/ambulances-mercedes-centre.webp";
import audiR8Banc from "@/assets/centre/audi-r8-banc.webp";
import bancControleCentre from "@/assets/centre/banc-controle-centre.webp";
import benelli750 from "@/assets/centre/benelli-750-collection.webp";
import bmwTaxiBanc from "@/assets/centre/bmw-taxi-banc.webp";
import ducatiStreetfighter from "@/assets/centre/ducati-streetfighter.webp";
import facadeCentreEte from "@/assets/centre/facade-centre-ete.webp";
import facadeCentre from "@/assets/centre/facade-centre.webp";
import ferrari308 from "@/assets/centre/ferrari-308-facade.webp";
import golfRBanc from "@/assets/centre/golf-r-banc-controle.webp";
import heroAutosur from "@/assets/centre/hero-autosur.webp";
import lamborghiniUrus from "@/assets/centre/lamborghini-urus-banc.webp";
import lexusBanc from "@/assets/centre/lexus-banc.webp";
import mercedesTaxis from "@/assets/centre/mercedes-taxis.webp";
import miniRedbull from "@/assets/centre/mini-redbull-autosur.webp";
import rollsRoyce from "@/assets/centre/rolls-royce-facade.webp";
import yamahaR1 from "@/assets/centre/yamaha-r1.webp";

const galleryImages = [
  // Avec véhicules (mixé)
  { src: ferrari308, alt: "Ferrari 308 devant le centre Autosur Montreuil" },
  { src: yamahaR1, alt: "Yamaha R1 au centre Autosur Montreuil" },
  { src: lamborghiniUrus, alt: "Lamborghini Urus sur banc de contrôle Autosur Montreuil" },
  { src: ambulancesMercedes, alt: "Ambulances Mercedes au centre Autosur Montreuil" },
  { src: rollsRoyce, alt: "Rolls-Royce devant le centre Autosur Montreuil" },
  { src: scootersAccueil, alt: "Scooters à l'accueil du centre Autosur Montreuil" },
  { src: golfRBanc, alt: "Volkswagen Golf R sur banc de contrôle Autosur Montreuil" },
  { src: benelli750, alt: "Benelli 750 de collection au centre Autosur Montreuil" },
  { src: audiR8Banc, alt: "Audi R8 sur banc de contrôle Autosur Montreuil" },
  { src: miniRedbull, alt: "Mini Red Bull au centre Autosur Montreuil" },
  { src: mercedesTaxis, alt: "Mercedes taxis au centre Autosur Montreuil" },
  { src: audiRs6Banc, alt: "Audi RS6 sur banc de freinage Autosur Montreuil" },
  { src: bmwTaxiBanc, alt: "BMW taxi sur banc de contrôle Autosur Montreuil" },
  { src: lexusBanc, alt: "Lexus sur banc de contrôle Autosur Montreuil" },
  // Sans véhicules
  { src: facadeCentre, alt: "Façade du centre Autosur Montreuil-sous-Bois" },
  { src: facadeCentreEte, alt: "Façade du centre Autosur Montreuil en été" },
  { src: facadeCentreEntree, alt: "Entrée du centre Autosur Montreuil-sous-Bois" },
  { src: accueilReception, alt: "Accueil et réception du centre Autosur Montreuil-sous-Bois" },
  { src: bancControleCentre, alt: "Banc de contrôle technique Autosur Montreuil" },
];

const values = [
  { 
    icon: Shield, 
    title: "Expertise certifiée", 
    description: "Centre agréé par l'État, équipements homologués et contrôleurs diplômés." 
  },
  { 
    icon: Award, 
    title: "Qualité Autosur", 
    description: "Marque préférée des Français, élue meilleure enseigne de contrôle technique." 
  },
  { 
    icon: Users, 
    title: "Équipe dévouée", 
    description: "Des professionnels à votre écoute pour vous conseiller et vous accompagner." 
  },
];

const equipments = [
  "Banc de freinage dernière génération",
  "Analyseur de gaz homologué",
  "Réglophare électronique",
  "Pont élévateur véhicules légers",
  "Équipement contrôle 2 roues",
  "Système informatique connecté",
];

const centreTestimonials = [
  { author: "Hakim Ouarezki", city: "Montreuil-sous-Bois", text: "Je recommande vivement ce centre de contrôle technique, Mr Yacine et son équipe sont vraiment adorables. Ils sont à l'écoute et font tout leur possible pour nous trouver un créneau !", rating: 5, date: "2026-01-20" },
  { author: "Dannywilde", city: "Montreuil-sous-Bois", text: "De vrais professionnels du contrôle technique, avec une authentique expertise mécanique et en plus chaleureux et sympathiques. Ils ont définitivement ma confiance.", rating: 5, date: "2025-06-20" },
  { author: "Caroline Dilasser", city: "Montreuil-sous-Bois", text: "À l'heure. Efficace et professionnel ! J'y vais depuis 5 ans et c'est toujours avec plaisir !", rating: 5, date: "2025-09-30" },
  { author: "Christophe Solas", city: "Montreuil-sous-Bois", text: "Très bon centre avec des jeunes expérimentés et très sympas. J'y vais régulièrement pour ma voiture et mon scooter.", rating: 5, date: "2025-09-14" },
  { author: "Am Gil", city: "Montreuil-sous-Bois", text: "Très content de ma visite chez Autosur, un très bon accueil, à l'écoute, très rassurant, et professionnel. Mon véhicule a été pris en charge rapidement malgré un calendrier chargé.", rating: 5, date: "2025-07-08" },
  { author: "Bastien Juny", city: "Montreuil-sous-Bois", text: "Je vais là-bas depuis des années et je vais continuer ! Les deux techniciens sont professionnels, accueillants et sympas ! Rapport qualité prix au top !", rating: 5, date: "2025-06-10" },
];

const LeCentrePage = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const nextImage = () => setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  const prevImage = () => setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);

  return (
    <SiteLayout>
      <SEOHead
        title="Notre Centre"
        description="Découvrez notre centre de contrôle technique à Montreuil : 20 ans d'expertise, équipements dernière génération, équipe certifiée. Parking gratuit, accès PMR."
        path="/le-centre"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Le Centre", href: "/le-centre" }]} />
      
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${scootersAccueil})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              À PROPOS
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Le Centre Autosur Montreuil-sous-Bois
            </h1>
            <p className="font-body text-white/80 text-lg">
              Un centre de contrôle technique moderne et accueillant, au service de votre sécurité depuis plus de 20 ans.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Présentation */}
      <section className="py-14 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-end">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-6">
                Bienvenue dans votre centre de contrôle technique
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                <p>
                  Toute l'équipe de votre centre <strong className="text-autosur-navy">AUTOSUR Montreuil-sous-Bois</strong> est 
                  à votre disposition pour vous apporter des conseils de sécurité et effectuer le contrôle technique de 
                  votre véhicule.
                </p>
                <p>
                  Votre centre vous garantit un contrôle de qualité dans le respect de l'environnement. 
                  Nos équipements modernes et notre équipe qualifiée vous assurent un service rapide et professionnel.
                </p>
                <p>
                  Situé au 133, Boulevard Aristide Briand, notre centre est facilement accessible et dispose 
                  d'un parking sécurisé pour votre confort.
                </p>
              </div>

              {/* Rating */}
              <a
                href="https://trustville.com/avis-clients/pc/cyvO9g/controle_technique_/autosur_montreuil_sous_bois"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 p-6 bg-gray-50 rounded-2xl block hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((star) => (
                      <Star key={star} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <div>
                    <p className="font-heading font-bold text-autosur-navy">4.9/5</p>
                    <p className="font-body text-muted-foreground text-sm">40 avis clients</p>
                  </div>
                </div>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={facadeCentreEntree}
                  alt="Entrée du centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Equipments - AVANT engagements */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src={audiRs6Banc}
                  alt="Audi RS6 sur banc de freinage au centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
                NOS ÉQUIPEMENTS
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-6">
                Des équipements modernes et homologués
              </h2>
              <p className="font-body text-muted-foreground text-lg mb-8">
                Notre centre est équipé des dernières technologies pour garantir des contrôles 
                précis et conformes à la réglementation.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {equipments.map((equipment, index) => (
                  <motion.div
                    key={equipment}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="font-body text-autosur-navy">{equipment}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nos engagements */}
      <section className="pt-20 pb-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-4">
              Nos engagements
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              La qualité et le professionnalisme au service de votre sécurité
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-autosur-blue/10 rounded-2xl flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-autosur-blue" />
                </div>
                <h3 className="font-heading font-bold text-autosur-navy text-xl mb-4">
                  {value.title}
                </h3>
                <p className="font-body text-muted-foreground">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10 text-center"
          >
            <img 
              src={logoAutosurMarquePreferee}
              alt="Autosur - Marque préférée des Français"
              className="max-w-[220px] w-full h-auto mx-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Galerie photos */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-xs mb-3 block">
              GALERIE PHOTOS
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-4">
              Découvrez notre centre en images
            </h2>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-lg relative group">
              <img
                src={galleryImages[currentImage].src}
                alt={galleryImages[currentImage].alt}
                className="w-full h-full object-cover transition-opacity duration-500"
              />
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                aria-label="Photo précédente"
              >
                <ChevronLeft className="w-5 h-5 text-autosur-navy" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                aria-label="Photo suivante"
              >
                <ChevronRight className="w-5 h-5 text-autosur-navy" />
              </button>
              <div className="absolute bottom-3 right-3 bg-autosur-navy/70 backdrop-blur-sm text-white font-body text-sm px-3 py-1 rounded-full">
                {currentImage + 1} / {galleryImages.length}
              </div>
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {galleryImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentImage
                      ? "border-autosur-red shadow-md scale-105"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img.src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel
        testimonials={centreTestimonials}
        title="Ce que disent nos clients"
        subtitle="343 avis Google • Note 4.9/5"
      />

      {/* Map section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-4">
              Nous trouver
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Info cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="space-y-4"
            >
              <a 
                href="https://maps.app.goo.gl/QMbfid7TsPVr8ZB76"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 block hover:shadow-md hover:border-autosur-blue/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-autosur-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-autosur-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">Adresse</h3>
                    <p className="font-body text-muted-foreground">
                      133, Boulevard Aristide Briand<br />
                      93100 Montreuil-sous-Bois
                    </p>
                  </div>
                </div>
              </a>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-autosur-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-autosur-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">Téléphone</h3>
                    <a href="tel:+33149889240" className="font-body text-autosur-blue hover:text-autosur-navy transition-colors">
                      01 49 88 92 40
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-autosur-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-autosur-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">Horaires</h3>
                    <div className="font-body text-muted-foreground text-sm space-y-1">
                      <p>Lundi - Vendredi: 8h30 - 18h30</p>
                      <p>Samedi: 9h00 - 13h00</p>
                      <p className="text-autosur-red">Dimanche: Fermé</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-2"
            >
              <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-lg bg-gray-200">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.215933153219!2d2.4535329999999997!3d48.873160000000006!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66d4a173a4aa9%3A0xd973599b099949f6!2sAUTOSUR%20MOTOSUR%20Contr%C3%B4le%20Technique%20AUTO%20%26%20MOTO!5e0!3m2!1sfr!2sfr!4v1770576435387!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation Autosur Montreuil-sous-Bois"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default LeCentrePage;
